import '@xyflow/react/dist/style.css';

import { Connection, EdgeTypes, NodeTypes, useReactFlow } from '@xyflow/react';
import {
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import { useTheme } from '@/app/ThemeContext';
import { FlowEdge } from '@/sections/insights/queryBuilder/components/ReactFlow/FlowEdge';
import { FlowNode } from '@/sections/insights/queryBuilder/components/ReactFlow/FlowNode';
import { useQueryData } from '@/sections/insights/queryBuilder/state/useQueryData';
import { getIconOrDefault } from '@/sections/insights/queryBuilder/utils/graphIcons';
import { generateUuid } from '@/utils/uuid.util';

import { useGraphLayout } from './hooks/useGraphLayout';
import { useReactFlowState } from './hooks/useReactFlowState';
import { QueryVisualizerCanvas } from './QueryVisualizerCanvas';
import { QueryVisualizerToolbar } from './QueryVisualizerToolbar';

const nodeTypes: NodeTypes = {
  entity: FlowNode,
};

const edgeTypes: EdgeTypes = {
  relationship: FlowEdge,
};

/**
 * QueryVisualizer - ReactFlow graph visualization component
 *
 * Displays query structure as an interactive graph with:
 * - Nodes representing entities with icons
 * - Edges representing relationships with labels
 * - Auto-centering on query changes
 * - Dagre auto-layout for readable structure
 * - Interactive pan/zoom/drag capabilities
 * - Controlled mode for dynamic node/edge addition
 * - Ref-based auto-arrange via layoutGraphRef
 *
 * IMPORTANT: Parent components must wrap this with:
 * 1. <QueryEditorProvider initialGraph={flatGraph}>
 * 2. <ReactFlowProvider>
 */
export const QueryVisualizer = ({
  editable = true,
  isPlacementMode = false,
  onNodePlaced,
  layoutGraphRef,
}: QueryVisualizerProps) => {
  const { screenToFlowPosition } = useReactFlow();
  const { currentTheme } = useTheme();

  // Get data from QueryDataContext
  const { queryState, dispatch } = useQueryData();
  const flatGraph = queryState?.flatGraph;

  // Get default icon for ghost node only when in placement mode
  const ghostIconUrl = useMemo(() => {
    if (!isPlacementMode) return null;
    const { icon } = getIconOrDefault(undefined, currentTheme);
    return icon;
  }, [isPlacementMode, currentTheme]);

  // ReactFlow state management hook
  const {
    rfNodes,
    rfEdges,
    expandedModalIds,
    cursorPosition,
    onNodesChange,
    toggleModalExpanded,
    setCursorPosition,
    setRfNodes,
    setRfEdges,
  } = useReactFlowState({
    flatGraph,
    dispatch,
    editable,
    isPlacementMode,
  });

  // Graph layout hook
  const { applyLayout, fitViewOptions } = useGraphLayout({
    flatGraph,
    dispatch,
    editable,
    expandedModalIds,
    toggleModalExpanded,
    setRfNodes,
    setRfEdges,
    layoutGraphRef,
  });

  // Track cursor position when in placement mode
  useEffect(() => {
    if (!isPlacementMode) {
      return;
    }

    const handleMouseMove = (e: globalThis.MouseEvent) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [isPlacementMode, setCursorPosition]);

  // Canvas click handler for node placement
  const handleCanvasClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!isPlacementMode) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const nodeId = `node-${generateUuid()}`;

      dispatch({
        type: 'ADD_NODE',
        node: {
          id: nodeId,
          data: { labels: [], filters: [] },
          uiState: { position },
        },
      });

      onNodePlaced?.();
    },
    [isPlacementMode, dispatch, screenToFlowPosition, onNodePlaced]
  );

  // Edge creation handler
  const handleConnect = useCallback(
    (connection: Connection) => {
      const edgeId = `edge-${generateUuid()}`;

      dispatch({
        type: 'ADD_EDGE',
        edge: {
          id: edgeId,
          sourceId: connection.source!,
          targetId: connection.target!,
          data: {
            label: [],
            optional: false,
            length: undefined,
          },
          direction: 'target',
        },
      });
    },
    [dispatch]
  );

  // Escape key handler for placement mode cancellation
  useEffect(() => {
    if (!isPlacementMode) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onNodePlaced?.(); // Cancel placement mode
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isPlacementMode, onNodePlaced]);

  return (
    <QueryVisualizerCanvas
      rfNodes={rfNodes}
      rfEdges={rfEdges}
      onNodesChange={onNodesChange}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      editable={editable}
      isPlacementMode={isPlacementMode}
      onCanvasClick={handleCanvasClick}
      onConnect={handleConnect}
      fitViewOptions={fitViewOptions}
      ghostIconUrl={ghostIconUrl}
      cursorPosition={cursorPosition}
    >
      <QueryVisualizerToolbar onFitView={() => applyLayout()} />
    </QueryVisualizerCanvas>
  );
};

interface QueryVisualizerProps {
  editable?: boolean;
  isPlacementMode?: boolean;
  onNodePlaced?: () => void;
  layoutGraphRef?: MutableRefObject<(() => void) | null>; // Ref for auto-arrange
}
